/**
 * GamificationContext
 * 
 * Provides global gamification state and celebration triggers.
 * Wraps the app to enable points tracking, achievements, and celebrations.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { 
  getUserGamificationProfile, 
  subscribeToGamificationProfile,
  updateLoginStreak,
  awardPoints,
  shouldCelebrate,
  getUserLevel,
  getNextLevel,
  initializeLevelsCache,
  POINT_VALUES,
  ACHIEVEMENTS
} from '../../utils/gamificationUtils';
import { 
  CelebrationOverlay, 
  PointsPopup, 
  AchievementUnlockedModal,
  LevelUpModal,
  StreakNotification 
} from '../gamification/CelebrationOverlay';

const GamificationContext = createContext();

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export const GamificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Celebration state
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [currentCelebration, setCurrentCelebration] = useState(null);
  const [pointsPopup, setPointsPopup] = useState({ show: false, points: 0 });
  const [streakNotification, setStreakNotification] = useState({ show: false, streak: 0, bonus: 0 });
  
  // Subscribe to profile updates
  useEffect(() => {
    if (!currentUser?.uid) {
      setProfile(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    // Initialize levels cache from Firestore (for level progression)
    initializeLevelsCache().catch(console.error);
    
    // Initial load
    getUserGamificationProfile(currentUser.uid)
      .then(setProfile)
      .catch(console.error)
      .finally(() => setLoading(false));
    
    // Real-time subscription
    const unsubscribe = subscribeToGamificationProfile(currentUser.uid, (data) => {
      setProfile(data);
    });
    
    return () => unsubscribe();
  }, [currentUser?.uid]);
  
  // Update login streak on mount (once per session)
  useEffect(() => {
    if (!currentUser?.uid || !profile) return;
    
    const sessionKey = `gamification_login_${new Date().toDateString()}`;
    if (sessionStorage.getItem(sessionKey)) return;
    
    updateLoginStreak(currentUser.uid)
      .then(({ streak, bonus }) => {
        sessionStorage.setItem(sessionKey, 'true');
        if (bonus > 0) {
          setStreakNotification({ show: true, streak, bonus });
        }
      })
      .catch(console.error);
  }, [currentUser?.uid, profile?.userId]);
  
  // Process celebration queue
  useEffect(() => {
    if (celebrationQueue.length > 0 && !currentCelebration) {
      const next = celebrationQueue[0];
      setCurrentCelebration(next);
      setCelebrationQueue(prev => prev.slice(1));
    }
  }, [celebrationQueue, currentCelebration]);
  
  // Award points with celebration
  const recordActivity = useCallback(async (action, metadata = {}) => {
    if (!currentUser?.uid) {
      console.warn('[Gamification] No user logged in, skipping activity:', action);
      return null;
    }
    
    try {
      const result = await awardPoints(currentUser.uid, action, metadata);
      
      if (result.pointsAwarded > 0) {
        // Show points popup
        setPointsPopup({ show: true, points: result.pointsAwarded });
        setTimeout(() => setPointsPopup({ show: false, points: 0 }), 2500);
        
        // Queue celebrations
        const celebration = shouldCelebrate(result);
        
        if (result.levelUp) {
          setCelebrationQueue(prev => [...prev, {
            type: 'level_up',
            oldLevel: result.oldLevel,
            newLevel: result.newLevel
          }]);
        }
        
        if (result.newAchievements?.length > 0) {
          result.newAchievements.forEach(achievement => {
            setCelebrationQueue(prev => [...prev, {
              type: 'achievement',
              achievement
            }]);
          });
        } else if (celebration.celebrate && celebration.type === 'points' && celebration.intensity !== 'low') {
          setCelebrationQueue(prev => [...prev, {
            type: 'points',
            intensity: celebration.intensity,
            points: result.pointsAwarded
          }]);
        }
      }
      
      return result;
    } catch (error) {
      console.error('Failed to record activity:', error);
      return null;
    }
  }, [currentUser?.uid]);
  
  // Clear current celebration
  const dismissCelebration = useCallback(() => {
    setCurrentCelebration(null);
  }, []);
  
  // Computed values
  const currentLevel = profile ? getUserLevel(profile.totalPoints || 0) : null;
  const nextLevelInfo = profile ? getNextLevel(profile.totalPoints || 0) : null;
  
  const value = {
    profile,
    loading,
    currentLevel,
    nextLevelInfo,
    recordActivity,
    POINT_VALUES,
    ACHIEVEMENTS
  };
  
  return (
    <GamificationContext.Provider value={value}>
      {children}
      
      {/* Points Popup */}
      <PointsPopup 
        points={pointsPopup.points} 
        show={pointsPopup.show} 
        position="top-right"
      />
      
      {/* Streak Notification */}
      <StreakNotification 
        streak={streakNotification.streak}
        bonus={streakNotification.bonus}
        show={streakNotification.show}
        onDismiss={() => setStreakNotification({ show: false, streak: 0, bonus: 0 })}
      />
      
      {/* Level Up Modal */}
      <LevelUpModal 
        show={currentCelebration?.type === 'level_up'}
        oldLevel={currentCelebration?.oldLevel}
        newLevel={currentCelebration?.newLevel}
        onClose={dismissCelebration}
      />
      
      {/* Achievement Unlocked Modal */}
      <AchievementUnlockedModal 
        show={currentCelebration?.type === 'achievement'}
        achievement={currentCelebration?.achievement}
        onClose={dismissCelebration}
      />
      
      {/* Generic Celebration Overlay */}
      <CelebrationOverlay 
        show={currentCelebration?.type === 'points'}
        type="points"
        intensity={currentCelebration?.intensity || 'medium'}
        title="Great Work!"
        subtitle={`+${currentCelebration?.points || 0} points`}
        onComplete={dismissCelebration}
      />
    </GamificationContext.Provider>
  );
};

export default GamificationProvider;
